/**
 * Utilitários de data de calendário para a Agenda médica.
 * Evita o bug clássico de `new Date('YYYY-MM-DD')` (UTC midnight)
 * que desloca o dia em fusos como America/Sao_Paulo.
 */

export const APP_CALENDAR_TIMEZONE = 'America/Sao_Paulo';

/**
 * Formata um Date no fuso local do browser como YYYY-MM-DD
 * (usado para células do calendário construídas com `new Date(y, m, d)`).
 */
export function formatLocalDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Extrai a chave de calendário YYYY-MM-DD de um valor de consulta.
 * Strings só-data são usadas literalmente (sem parse UTC).
 */
export function toAppointmentDateKey(
  value: string | Date | null | undefined,
): string | null {
  if (value == null || value === '') return null;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return formatDateKeyInTimezone(value, APP_CALENDAR_TIMEZONE);
  }

  const trimmed = String(value).trim();
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (dateOnly) {
    return `${dateOnly[1]}-${dateOnly[2]}-${dateOnly[3]}`;
  }

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return null;
  return formatDateKeyInTimezone(parsed, APP_CALENDAR_TIMEZONE);
}

export function formatDateKeyInTimezone(
  date: Date,
  timeZone: string = APP_CALENDAR_TIMEZONE,
): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

export function appointmentsMatchCalendarDay<T extends { date?: string }>(
  appointments: T[],
  day: Date,
): T[] {
  const dayKey = formatLocalDateKey(day);
  return appointments.filter((apt) => toAppointmentDateKey(apt.date) === dayKey);
}
